import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronLeft, ChevronRight, Play, Pause } from 'lucide-react';

// System videos already available in the app
const STORIES = [
  {
    id: 'birinci',
    title: 'Bir Hayalin Doğuşu',
    subtitle: 'Bir markanın değil, bir hayalin doğuşunu izliyorsunuz.',
    posterCaption: 'Marka',
    videos: [
      {
        src: 'https://customer-assets.emergentagent.com/job_valmera-luxury/artifacts/b8m7yiyj_Bug%C3%BCn%20bir%20markan%C4%B1n%20do%C4%9Fu%C5%9Funu%20de%C4%9Fil%2C%20bir%20hayalin%20ger%C3%A7ekle%C5%9Fmesini%20izliyorsunuz.%20Bu%20s%C3%BCrecin%20nas%C4%B1l%20ad.mp4',
        caption: 'Bir Hayalin Doğuşu',
      },
    ],
    thumbnail: null,
  },
  {
    id: 'ikinci',
    title: '90×90 Tasarımlar',
    subtitle: 'Fularlarımız artık eşarp boyutunda da sizinle.',
    posterCaption: 'Koleksiyon',
    videos: [
      {
        src: 'https://customer-assets.emergentagent.com/job_valmera-luxury/artifacts/r94l9oth_Beklenen%20kargo%20a%C3%A7%C4%B1l%C4%B1m%C4%B1%20%F0%9F%8E%81%F0%9F%A7%9A%F0%9F%8F%BB_%E2%99%80%EF%B8%8F%F0%9F%A5%B3Sizler%20i%C3%A7in%20fularlar%C4%B1m%C4%B1z%C4%B1n%2090x90%20e%C5%9Farp%20boyutunda%20da%20tasar%C4%B1mlar.mp4',
        caption: '90×90 Yeni Boyut',
      },
    ],
    thumbnail: null,
  },
  {
    id: 'ucuncu',
    title: 'Safiye Ali',
    subtitle: 'Türk kadınlarının hikâyesi, ipeğin üstünde.',
    posterCaption: 'Tasarım',
    videos: [
      {
        src: 'https://customer-assets.emergentagent.com/job_valmera-luxury/artifacts/qpsh8fwe_%40valmeraofficial%20fularlar%C4%B1nda%20T%C3%BCrk%20Kad%C4%B1nlar%C4%B1n%C4%B1%20anlat%C4%B1yor.Safiye%20Ali%20tasar%C4%B1m%C4%B1m%C4%B1z%20sizinle......%23lu.mp4',
        caption: 'Safiye Ali Tasarımı',
      },
    ],
    thumbnail: null,
  },
];

// ============= INSTAGRAM STORY VIEWER =============
const StoryViewer = ({ stories, startIndex = 0, onClose }) => {
  const [storyIdx, setStoryIdx] = useState(startIndex);
  const [videoIdx, setVideoIdx] = useState(0);
  const [progress, setProgress] = useState(0);
  const [paused, setPaused] = useState(false);
  const videoRef = useRef(null);

  const currentStory = stories[storyIdx];
  const currentVideo = currentStory?.videos[videoIdx];

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  useEffect(() => {
    setProgress(0);
    setVideoIdx(0);
  }, [storyIdx]);

  useEffect(() => {
    setProgress(0);
  }, [videoIdx]);

  const handleTimeUpdate = () => {
    if (videoRef.current && videoRef.current.duration) {
      setProgress((videoRef.current.currentTime / videoRef.current.duration) * 100);
    }
  };

  const handleVideoEnd = () => {
    if (videoIdx < currentStory.videos.length - 1) {
      setVideoIdx(videoIdx + 1);
    } else if (storyIdx < stories.length - 1) {
      setStoryIdx(storyIdx + 1);
    } else {
      onClose();
    }
  };

  const goPrev = () => {
    if (videoIdx > 0) setVideoIdx(videoIdx - 1);
    else if (storyIdx > 0) setStoryIdx(storyIdx - 1);
  };

  const goNext = () => {
    if (videoIdx < currentStory.videos.length - 1) setVideoIdx(videoIdx + 1);
    else if (storyIdx < stories.length - 1) setStoryIdx(storyIdx + 1);
    else onClose();
  };

  const togglePause = () => {
    if (!videoRef.current) return;
    if (paused) videoRef.current.play();
    else videoRef.current.pause();
    setPaused(!paused);
  };

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'ArrowRight') goNext();
      else if (e.key === 'ArrowLeft') goPrev();
      else if (e.key === 'Escape') onClose();
      else if (e.key === ' ') { e.preventDefault(); togglePause(); }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
    // eslint-disable-next-line
  }, [storyIdx, videoIdx, paused]);

  if (!currentStory || !currentVideo) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
        className="fixed inset-0 z-[300] bg-black flex items-center justify-center"
        data-testid="ig-story-viewer"
      >
        {/* Close */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 md:top-6 md:right-6 z-[320] w-10 h-10 flex items-center justify-center text-white/90 hover:bg-white/10 rounded-full"
          data-testid="close-ig-story"
          aria-label="Kapat"
        >
          <X size={24} />
        </button>

        {/* Story area (9:16 portrait) */}
        <div className="relative w-full h-full md:w-[420px] md:h-[90vh] md:max-h-[820px] bg-black overflow-hidden md:rounded-2xl md:shadow-[0_30px_80px_-10px_rgba(0,0,0,0.9)]">
          {/* Video */}
          <video
            ref={videoRef}
            key={currentVideo.src}
            src={currentVideo.src}
            autoPlay
            playsInline
            onTimeUpdate={handleTimeUpdate}
            onEnded={handleVideoEnd}
            className="absolute inset-0 w-full h-full object-cover"
          />

          {/* Dark gradient overlays */}
          <div className="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-black/70 to-transparent pointer-events-none" />
          <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-black/70 to-transparent pointer-events-none" />

          {/* Progress bars */}
          <div className="absolute top-3 left-3 right-3 flex gap-1 z-20">
            {currentStory.videos.map((_, i) => (
              <div key={i} className="flex-1 h-0.5 bg-white/30 overflow-hidden rounded-full">
                <div
                  className="h-full bg-white transition-all"
                  style={{
                    width: i < videoIdx ? '100%' : i === videoIdx ? `${progress}%` : '0%',
                  }}
                />
              </div>
            ))}
          </div>

          {/* Story header */}
          <div className="absolute top-6 left-4 right-14 z-10 flex items-center gap-3 mt-2">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-amber-400 via-rose-400 to-purple-500 p-[2px]">
              <div className="w-full h-full rounded-full bg-white overflow-hidden">
                <img
                  src="https://customer-assets-cm19k8pv.emergentagent.net/job_3f3acde8-bbff-4f28-a1ca-d72be186d0f6/artifacts/6bz19s82_Valmera%20Logo%20%281%29.png"
                  alt="Valméra"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            <div className="text-white flex-1">
              <div className="text-sm font-semibold">valmeraofficial</div>
              <div className="text-[10px] opacity-70 tracking-wider">{currentStory.posterCaption}</div>
            </div>
            <button
              onClick={togglePause}
              className="text-white/80 hover:text-white p-1"
              data-testid="pause-ig-story"
            >
              {paused ? <Play size={16} fill="currentColor" /> : <Pause size={16} fill="currentColor" />}
            </button>
          </div>

          {/* Tap zones (left = prev, right = next) */}
          <button
            onClick={goPrev}
            className="absolute left-0 top-0 bottom-0 w-1/3 z-10 group"
            data-testid="ig-story-prev"
            aria-label="Önceki"
          >
            <div className="hidden md:flex absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/40 items-center justify-center opacity-0 group-hover:opacity-100 transition">
              <ChevronLeft size={16} className="text-white" />
            </div>
          </button>
          <button
            onClick={goNext}
            className="absolute right-0 top-0 bottom-0 w-1/3 z-10 group"
            data-testid="ig-story-next"
            aria-label="Sonraki"
          >
            <div className="hidden md:flex absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/40 items-center justify-center opacity-0 group-hover:opacity-100 transition">
              <ChevronRight size={16} className="text-white" />
            </div>
          </button>

          {/* Caption at bottom */}
          <div className="absolute inset-x-0 bottom-6 px-6 z-10 text-white text-center pointer-events-none">
            <div className="text-[10px] tracking-[0.4em] mb-2 opacity-70">— {currentStory.title.toUpperCase()} —</div>
            <p className="text-sm md:text-base font-light italic max-w-sm mx-auto">
              {currentStory.subtitle}
            </p>
          </div>
        </div>

        {/* Sideways navigation on desktop */}
        <button
          onClick={goPrev}
          disabled={storyIdx === 0 && videoIdx === 0}
          className="hidden md:flex absolute left-8 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white items-center justify-center disabled:opacity-20"
        >
          <ChevronLeft size={24} />
        </button>
        <button
          onClick={goNext}
          className="hidden md:flex absolute right-8 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white items-center justify-center"
        >
          <ChevronRight size={24} />
        </button>
      </motion.div>
    </AnimatePresence>
  );
};

// ============= INSTAGRAM STORY REEL (avatars row) =============
const InstagramStorySection = () => {
  const [openIdx, setOpenIdx] = useState(null);

  return (
    <section className="py-20 md:py-28 bg-[#0a0a0a] text-white overflow-hidden" data-testid="ig-story-section">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-14 md:mb-16">
          <div className="text-xs tracking-[0.5em] opacity-60 mb-4">— VALMÉRA HİKAYESİ —</div>
          <h2 className="font-serif text-4xl md:text-6xl font-light mb-6 leading-tight">
            Marka <span className="italic font-thin">Hikayeleri</span>
          </h2>
          <p className="text-white/60 max-w-2xl mx-auto italic text-sm md:text-base">
            Bir avatarı seç, tam ekran hikâyeye başla. Sağ / sol tıkla, boşluk ile duraklat.
          </p>
        </div>

        {/* Instagram-style circular story avatars */}
        <div className="flex flex-wrap items-center justify-center gap-8 md:gap-12">
          {STORIES.map((story, i) => (
            <button
              key={story.id}
              onClick={() => setOpenIdx(i)}
              className="flex flex-col items-center group cursor-pointer"
              data-testid={`ig-story-avatar-${story.id}`}
            >
              <div className="relative w-24 h-24 md:w-32 md:h-32 rounded-full p-[3px] bg-gradient-to-br from-amber-400 via-rose-400 to-purple-500 transition-transform group-hover:scale-110 duration-500">
                <div className="w-full h-full rounded-full bg-black p-[3px]">
                  <div className="w-full h-full rounded-full overflow-hidden bg-gradient-to-br from-gray-900 to-black relative">
                    {/* Video preview */}
                    <video
                      src={story.videos[0].src}
                      autoPlay
                      loop
                      muted
                      playsInline
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                    {/* Play overlay */}
                    <div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/10 transition-colors">
                      <div className="w-8 h-8 rounded-full bg-white/20 backdrop-blur-sm border border-white/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <Play size={12} className="text-white ml-0.5" fill="currentColor" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="mt-4 text-white text-sm font-medium max-w-[130px] text-center leading-tight">
                {story.title}
              </div>
              <div className="mt-1 text-[10px] tracking-[0.3em] text-amber-200/60 uppercase">
                {story.posterCaption}
              </div>
            </button>
          ))}
        </div>

        <p className="text-center text-white/40 text-[10px] tracking-[0.4em] mt-14 italic">
          — bir avatara dokunun — hikâye başlasın —
        </p>
      </div>

      {/* Fullscreen story viewer */}
      {openIdx !== null && (
        <StoryViewer
          stories={STORIES}
          startIndex={openIdx}
          onClose={() => setOpenIdx(null)}
        />
      )}
    </section>
  );
};

export default InstagramStorySection;
